import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Suspense } from 'react';
import type { ReactNode } from 'react';
import { TooltipProvider } from '@/components/ui/tooltip';
import { ThemeProvider } from '@/components/providers';
import { Toaster } from 'sonner';
import { useAuth } from '@/hooks/useAuth';
import { AuthProvider } from '@/contexts/AuthContext';
import { isAdminRole, isPatientRole } from '@/lib/roleRouting';

console.log('🚀 App.tsx carregado');

// Pages - imports diretos para menos erros
import LoginPage from './app/login/page';
import LandingPage from './app/landing/page';
import RegisterPage from './app/register/page';
import DashboardPage from './app/dashboard/page';
import NotificationsPage from './app/notifications/page';
import ConsultasPage from './app/consultas/page';
import MinhasConsultasPage from './app/minhas-consultas/page';
import FilaRenovacoesPage from './app/fila-renovacoes/page';
import MeusDocumentosPage from './app/meus-documentos/page';
import MeusMedicosPage from './app/meus-medicos/page';
import AdminPage from './app/admin/page';
import DoctorApprovalPage from './app/admin/aprovacao-medicos/page';
import AuditLogsPage from './app/admin/audit-logs/page';
import BuscarMedicosPage from './app/buscar-medicos/page';
import AgendamentoPage from './app/agendamento/page';
import ChatPage from './app/chat/page';
import PublicDoctorProfilePage from './app/medico/[id]/page';
import AgendaPage from './app/agenda/page';
import PatientsPage from './app/pacientes/page';
import TemplatesPage from './app/templates/page';
import MascarasPage from './app/mascaras/page';
import OnDemandTriagePage from './app/on-demand/triage/page';
import OnDemandWaitingPage from './app/on-demand/waiting/page';
import InitialChecklistPage from './app/onboarding/checklist/page';
import DoctorChecklistPage from './app/onboarding/doctor-checklist/page';
import EHRPage from './app/prontuario/page';
import SettingsPage from './app/settings/page';
import MeuPerfilPage from './app/perfil/page';
import AnalyticsPage from './app/admin/analytics/page';
import PaymentResultPage from './app/payment/result/page';

import { ComingSoonPage } from './components/common/index';
import { RatingInterceptor } from './components/ratings/RatingInterceptor';

// Guards
import ProtectedRoute from './components/auth/ProtectedRoute';
import { PatientOnboardingGate } from './components/auth/PatientOnboardingGate';
import { DoctorOnboardingGate } from './components/auth/DoctorOnboardingGate';

const AnalyticsRouteSelector = () => {
  const { user } = useAuth();
  if (isAdminRole(user?.role)) {
    return <AnalyticsPage />;
  }
  return <ComingSoonPage title="Análises e Relatórios" />;
};

const DashboardRouteSelector = () => {
  const { user } = useAuth();

  if (user && isPatientRole(user.role)) {
    return (
      <PatientOnboardingGate user={user}>
        <DashboardPage />
      </PatientOnboardingGate>
    );
  }

  return (
    <DoctorOnboardingGate user={user}>
      <DashboardPage />
    </DoctorOnboardingGate>
  );
};

console.log('✅ Todos os componentes importados com sucesso');

const LoadingFallback = () => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
    <div>Carregando...</div>
  </div>
);

/**
 * Componente para redirecionar /prontuarios de acordo com o papel do usuário
 */
const ProntuariosRedirect = () => {
  const { user } = useAuth();

  if (isAdminRole(user?.role)) {
    return <Navigate to="/admin?tab=patients" replace />;
  }
  return <Navigate to="/pacientes" replace />;
};

const PatientProtectedRoute = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();

  return (
    <ProtectedRoute requiredRoles={['PATIENT']}>
      <PatientOnboardingGate user={user}>{children}</PatientOnboardingGate>
    </ProtectedRoute>
  );
};

const DoctorProtectedRoute = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();

  return (
    <ProtectedRoute requiredRoles={['DOCTOR']}>
      <DoctorOnboardingGate user={user}>{children}</DoctorOnboardingGate>
    </ProtectedRoute>
  );
};

/** Gate de onboarding para rotas compartilhadas (settings, chat, etc.). */
const WithOnboardingGates = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();

  return (
    <PatientOnboardingGate user={user}>
      <DoctorOnboardingGate user={user}>{children}</DoctorOnboardingGate>
    </PatientOnboardingGate>
  );
};

export default function App() {
  console.log('🔄 App renderizado');

  return (
    <>
      <Helmet>
        <title>App Saúde — Telemedicina e Atendimento Domiciliar</title>
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="icon" type="image/svg+xml" href="/favicon.svg" />
        <link rel="shortcut icon" href="/favicon.svg" />
        <html lang="pt-BR" />
      </Helmet>

      <AuthProvider>
        <Router>
          <ThemeProvider>
          <TooltipProvider>
            <RatingInterceptor />
            <Suspense fallback={<LoadingFallback />}>
              <Routes>
                {/* Public Routes */}
                <Route path="/" element={<LandingPage />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route path="/landing" element={<Navigate to="/" replace />} />
                <Route path="/medico/:id" element={<PublicDoctorProfilePage />} />

                {/* Protected Routes */}
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute>
                      <DashboardRouteSelector />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/notifications"
                  element={
                    <ProtectedRoute>
                      <NotificationsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/consultas"
                  element={
                    <DoctorProtectedRoute>
                      <ConsultasPage />
                    </DoctorProtectedRoute>
                  }
                />
                <Route
                  path="/fila-renovacoes"
                  element={
                    <DoctorProtectedRoute>
                      <FilaRenovacoesPage />
                    </DoctorProtectedRoute>
                  }
                />
                <Route
                  path="/minhas-consultas"
                  element={
                    <PatientProtectedRoute>
                      <MinhasConsultasPage />
                    </PatientProtectedRoute>
                  }
                />
                <Route
                  path="/buscar-medicos"
                  element={
                    <PatientProtectedRoute>
                      <BuscarMedicosPage />
                    </PatientProtectedRoute>
                  }
                />
                <Route
                  path="/payment/result"
                  element={
                    <ProtectedRoute>
                      <PaymentResultPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/agendamento"
                  element={
                    <PatientProtectedRoute>
                      <AgendamentoPage />
                    </PatientProtectedRoute>
                  }
                />
                <Route
                  path="/agendamento/:doctorId"
                  element={
                    <PatientProtectedRoute>
                      <AgendamentoPage />
                    </PatientProtectedRoute>
                  }
                />
                <Route
                  path="/meus-documentos"
                  element={
                    <PatientProtectedRoute>
                      <MeusDocumentosPage />
                    </PatientProtectedRoute>
                  }
                />
                <Route
                  path="/meus-medicos"
                  element={
                    <PatientProtectedRoute>
                      <MeusMedicosPage />
                    </PatientProtectedRoute>
                  }
                />
                <Route
                  path="/onboarding/checklist"
                  element={
                    <PatientProtectedRoute>
                      <InitialChecklistPage />
                    </PatientProtectedRoute>
                  }
                />
                <Route
                  path="/onboarding/doctor-checklist"
                  element={
                    <DoctorProtectedRoute>
                      <DoctorChecklistPage />
                    </DoctorProtectedRoute>
                  }
                />
                
                {/* On-Demand Patient Routes */}
                <Route
                  path="/on-demand/triage"
                  element={
                    <PatientProtectedRoute>
                      <OnDemandTriagePage />
                    </PatientProtectedRoute>
                  }
                />
                <Route
                  path="/on-demand/waiting"
                  element={
                    <PatientProtectedRoute>
                      <OnDemandWaitingPage />
                    </PatientProtectedRoute>
                  }
                />
                <Route
                  path="/admin"
                  element={
                    <ProtectedRoute requiredRoles={['ADMIN']}>
                      <AdminPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/analytics"
                  element={
                    <ProtectedRoute requiredRoles={['ADMIN']}>
                      <AnalyticsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/aprovacao-medicos"
                  element={
                    <ProtectedRoute requiredRoles={['ADMIN']}>
                      <DoctorApprovalPage />
                    </ProtectedRoute>
                  }
                />
                
                {/* Chat Route (Para Médico e Paciente) */}
                <Route
                  path="/chat/:appointmentId"
                  element={
                    <ProtectedRoute>
                      <WithOnboardingGates>
                        <ChatPage />
                      </WithOnboardingGates>
                    </ProtectedRoute>
                  }
                />

                {/* Admin and General Coming Soon Routes */}
                <Route path="/users" element={<ProtectedRoute requiredRoles={['ADMIN']}><ComingSoonPage title="Gerenciar Usuários" /></ProtectedRoute>} />
                <Route
                  path="/analytics"
                  element={
                    <ProtectedRoute requiredRoles={['ADMIN', 'DOCTOR', 'PATIENT']}>
                      <AnalyticsRouteSelector />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/relatorios"
                  element={
                    <ProtectedRoute requiredRoles={['ADMIN', 'DOCTOR', 'PATIENT']}>
                      <AnalyticsRouteSelector />
                    </ProtectedRoute>
                  }
                />
                <Route path="/admin/audit-logs" element={<ProtectedRoute requiredRoles={['ADMIN']}><AuditLogsPage /></ProtectedRoute>} />
                <Route path="/audit-logs" element={<ProtectedRoute requiredRoles={['ADMIN']}><Navigate to="/admin/audit-logs" replace /></ProtectedRoute>} />
                <Route
                  path="/settings"
                  element={
                    <ProtectedRoute requiredRoles={['ADMIN', 'DOCTOR', 'PATIENT']}>
                      <WithOnboardingGates>
                        <SettingsPage />
                      </WithOnboardingGates>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/perfil"
                  element={
                    <ProtectedRoute requiredRoles={['PATIENT']}>
                      <MeuPerfilPage />
                    </ProtectedRoute>
                  }
                />

                {/* Doctor Routes */}
                <Route
                  path="/pacientes"
                  element={
                    <DoctorProtectedRoute>
                      <PatientsPage />
                    </DoctorProtectedRoute>
                  }
                />
                <Route
                  path="/templates"
                  element={
                    <DoctorProtectedRoute>
                      <TemplatesPage />
                    </DoctorProtectedRoute>
                  }
                />
                <Route
                  path="/mascaras"
                  element={
                    <DoctorProtectedRoute>
                      <MascarasPage />
                    </DoctorProtectedRoute>
                  }
                />
                <Route
                  path="/agenda"
                  element={
                    <DoctorProtectedRoute>
                      <AgendaPage />
                    </DoctorProtectedRoute>
                  }
                />

                {/* EHR Routes */}
                <Route
                  path="/prontuario"
                  element={
                    <PatientProtectedRoute>
                      <EHRPage />
                    </PatientProtectedRoute>
                  }
                />
                <Route
                  path="/prontuarios"
                  element={
                    <ProtectedRoute requiredRoles={['DOCTOR', 'ADMIN']}>
                      <ProntuariosRedirect />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/prontuario/:pacienteId"
                  element={
                    <ProtectedRoute requiredRoles={['DOCTOR', 'ADMIN']}>
                      <EHRPage />
                    </ProtectedRoute>
                  }
                />

                {/* 404 Route */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>

            <Toaster position="top-right" theme="light" />
          </TooltipProvider>
        </ThemeProvider>
      </Router>
      </AuthProvider>
    </>
  );
}
